import { jsPDF } from "jspdf"
import { CoverLayoutService, UnitUtils } from "../core/print/cover_core.js"
import { calculateTextPositionsInContainer } from "../admin/cover_text_container.js"

export default class PDFGenerator {
    constructor() {
        this.initialize()
        this.bindEvents()
    }

    initialize() {
        // Dimensiones del tabloide en mm (en orientación landscape)
        // Tabloide: 11" x 17" = 279.4mm x 431.8mm
        // Tabloide: 47.5mm x 33mm
        this.tabloidWidth = 330 
        this.tabloidHeight = 475
        this.margin = 10 // Margen de seguridad de 1cm = 10mm

        // Obtener las variables globales
        this.photoWidth = window.PHOTO_WIDTH
        this.photoHeight = window.PHOTO_HEIGHT
        this.aspectRatio = window.ASPECT_RATIO
        this.totalPhotos = window.TOTAL_PHOTOS

        // Convertir dimensiones de píxeles a mm (asumiendo 300 DPI)
        const pixelsToMM = 25.4 / 300 // Factor de conversión de píxeles a mm
        this.photoWidth = this.photoWidth * pixelsToMM
        this.photoHeight = this.photoHeight * pixelsToMM

        console.log('PDF initialized:', {
            tabloid: {
                width: this.tabloidWidth,
                height: this.tabloidHeight
            },
            photo: {
                width: this.photoWidth,
                height: this.photoHeight
            },
            aspectRatio: this.aspectRatio,
            totalPhotos: this.totalPhotos
        })

        // Agregar el event listener al botón de generación
        const generateButton = document.querySelector('[data-action="generate-pdf"]')
        if (generateButton) {
            generateButton.addEventListener('click', () => this.generatePDF())
        }
    }

    bindEvents() {
        document.querySelectorAll('.package-tabloid-link').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault()
                this.handleTabloidClick(e.currentTarget)
            })
        })
    }

    handleTabloidClick(link) {
        // Obtener los datos del paquete desde los atributos data
        const title = link.dataset.title || 'tabloid-distribution'
        const quantity = Number(link.dataset.quantity)
        const aspectRatio = parseFloat(link.dataset.aspectRatio || '1.0')
        const width = parseFloat(link.dataset.width || '300')
        const height = parseFloat(link.dataset.height || '300')
        const format = link.dataset.format || 'standard'
        const order = link.dataset.order || '01-order-random-40'
        const usePhotos = link.dataset.usePhotos === 'true'

        // Mostrar mensaje de "Generando..."
        this.showGeneratingMessage(link)

        this.generatePDF({
            title,
            quantity,
            aspectRatio,
            width,
            height,
            format,
            order,
            usePhotos,
            button: link // Pasar referencia al botón
        })
    }

    async generatePDF(params) {
        const { title, quantity, aspectRatio, width, height, format, order, usePhotos, button } = params

        try {
            // Convertir dimensiones de píxeles a mm (asumiendo 300 DPI)
            const pixelsToMM = 25.4 / 300 // Factor de conversión de píxeles a mm
            const photoWidth = width * pixelsToMM
            const photoHeight = height * pixelsToMM

            // Cargar y convertir los logos SVG a PNG
            const logoImage = await this.loadAndConvertLogo('/lelab-logo.svg')
            const printedInImage = await this.loadAndConvertLogo('/printed-in.svg')

            // Si se deben usar fotos, cargarlas desde el servidor
            let photos = []
            if (usePhotos) {
                photos = await this.loadOrderPhotos()
            }

            // Calcular el área imprimible
            const printableWidth = this.tabloidWidth - (2 * this.margin)
            const printableHeight = this.tabloidHeight - (2 * this.margin)
            
            // Calcular cuántas fotos caben en el tabloide considerando el espaciado
            const maxPhotosPerRow = Math.floor(printableWidth / photoWidth)
            const maxPhotosPerColumn = Math.floor(printableHeight / photoHeight)

            // Validar que al menos cabe una foto
            if (maxPhotosPerRow < 1 || maxPhotosPerColumn < 1) {
                console.error('Las dimensiones de la foto son demasiado grandes para el área de impresión')
                return
            }

            // Calcular el espacio restante para los medianiles
            const remainingWidth = printableWidth - (maxPhotosPerRow * photoWidth)
            const remainingHeight = printableHeight - (maxPhotosPerColumn * photoHeight)

            // Calcular el tamaño de los medianiles dividiendo el espacio entre todas las filas/columnas
            const gutterWidth = remainingWidth / maxPhotosPerRow
            const gutterHeight = remainingHeight / maxPhotosPerColumn

            // Calcular el punto de inicio para centrar el grid
            const startX = this.margin + (gutterWidth / 2)
            const startY = this.margin + (gutterHeight / 2)

            // Calcular el número total de fotos que caben en una página
            const photosPerPage = maxPhotosPerRow * maxPhotosPerColumn

            // Calcular cuántas páginas necesitamos
            const totalPages = Math.ceil(quantity / photosPerPage)

            console.log('PDF generation:', {
                tabloid: {
                    width: this.tabloidWidth,
                    height: this.tabloidHeight
                },
                printable: {
                    width: printableWidth,
                    height: printableHeight
                },
                photo: {
                    width: photoWidth,
                    height: photoHeight
                },
                format: format,
                usePhotos: usePhotos,
                photosCount: photos.length,
                layout: {
                    photosPerRow: maxPhotosPerRow,
                    photosPerColumn: maxPhotosPerColumn,
                    totalPhotosPerPage: photosPerPage,
                    totalPages: totalPages,
                    gutters: {
                        width: gutterWidth,
                        height: gutterHeight
                    },
                    spacing: {
                        startX: startX,
                        startY: startY,
                        remainingWidth: remainingWidth,
                        remainingHeight: remainingHeight
                    }
                }
            })

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [this.tabloidWidth, this.tabloidHeight]
            })

            // Función para dibujar una cruz de recorte
            const drawCropMark = (x, y, size = 5) => {
                // Línea horizontal
                pdf.line(x - size, y, x + size, y)
                // Línea vertical
                pdf.line(x, y - size, x, y + size)
            }

            // Función para dibujar todas las guías de recorte en una página
            const drawCropMarks = () => {
                // Dibujar las guías de recorte para las columnas (eje X)
                for (let i = 0; i <= maxPhotosPerRow; i++) {
                    // Calcular la posición X del inicio y fin de cada bloque
                    const blockStartX = startX + (i * (photoWidth + gutterWidth))
                    const blockEndX = blockStartX + photoWidth

                    // Cruz superior (fuera del área de impresión)
                    drawCropMark(blockStartX, this.margin - 5)
                    drawCropMark(blockEndX, this.margin - 5)
                    // Cruz inferior (fuera del área de impresión)
                    drawCropMark(blockStartX, this.tabloidHeight - this.margin + 5)
                    drawCropMark(blockEndX, this.tabloidHeight - this.margin + 5)
                }

                // Dibujar las guías de recorte para las filas (eje Y)
                for (let i = 0; i <= maxPhotosPerColumn; i++) {
                    // Calcular la posición Y del inicio y fin de cada bloque
                    const blockStartY = startY + (i * (photoHeight + gutterHeight))
                    const blockEndY = blockStartY + photoHeight

                    // Cruz izquierda (fuera del área de impresión)
                    drawCropMark(this.margin - 5, blockStartY)
                    drawCropMark(this.margin - 5, blockEndY)
                    // Cruz derecha (fuera del área de impresión)
                    drawCropMark(this.tabloidWidth - this.margin + 5, blockStartY)
                    drawCropMark(this.tabloidWidth - this.margin + 5, blockEndY)
                }
            }

            let currentX = startX
            let currentY = startY
            let currentPage = 0
            let photosInCurrentRow = 0
            let photosInCurrentColumn = 0
            let photosPlaced = 0

            // Dibujar las guías en la primera página
            drawCropMarks()

            // Generar las páginas necesarias
            for (let i = 0; i < quantity; i++) {
                // Verificar si necesitamos una nueva página
                if (photosPlaced >= photosPerPage) {
                    pdf.addPage()
                    currentPage++
                    currentX = startX
                    currentY = startY
                    photosInCurrentRow = 0
                    photosInCurrentColumn = 0
                    photosPlaced = 0
                    // Dibujar las guías en la nueva página
                    drawCropMarks()
                }

                // Verificar si necesitamos una nueva fila
                if (photosInCurrentRow >= maxPhotosPerRow) {
                    currentX = startX
                    currentY += photoHeight + gutterHeight
                    photosInCurrentRow = 0
                    photosInCurrentColumn++
                }

                if (usePhotos && photos.length > 0) {
                    // Usar foto real
                    const photoIndex = i % photos.length
                    const photo = photos[photoIndex]
                    
                    if (photo && photo.dataUrl) {
                        try {
                            // Agregar la foto real
                            pdf.addImage(photo.dataUrl, 'JPEG', currentX, currentY, photoWidth, photoHeight)
                        } catch (error) {
                            console.error('Error adding photo to PDF:', error)
                            // Fallback: dibujar rectángulo gris
                            this.drawPhotoPlaceholder(pdf, currentX, currentY, photoWidth, photoHeight)
                        }
                    } else {
                        // Fallback: dibujar rectángulo gris
                        this.drawPhotoPlaceholder(pdf, currentX, currentY, photoWidth, photoHeight)
                    }
                } else {
                    // Dibujar un rectángulo para representar la foto (comportamiento original)
                    this.drawPhotoPlaceholder(pdf, currentX, currentY, photoWidth, photoHeight)
                }

                // Agregar el logo y número de orden solo cuando NO se usan fotos reales
                if (!usePhotos) {
                    // Agregar el logo
                    const logoSize = 10 // 1cm = 10mm
                    const logoX = currentX + (photoWidth - logoSize) / 2
                    const logoY = currentY + photoHeight - logoSize - 15 // Posicionar en la parte inferior
                    pdf.addImage(logoImage, 'PNG', logoX, logoY, logoSize, logoSize)

                    // Agregar el número de orden
                    pdf.setFontSize(8)
                    pdf.setTextColor(0, 0, 0)
                    const orderText = `#${order}`
                    const textWidth = pdf.getTextWidth(orderText)
                    const textX = currentX + (photoWidth - textWidth) / 2
                    const textY = logoY + logoSize + 5
                    pdf.text(orderText, textX, textY)
                }

                // Actualizar la posición para la siguiente foto
                currentX += photoWidth + gutterWidth
                photosInCurrentRow++
                photosPlaced++
            }

            try {
                pdf.save(`${title}.pdf`)
                
                // Mostrar mensaje de "¡Listo!"
                this.showReadyMessage(button)
            } catch (error) {
                console.error('Error generating PDF:', error)
                this.showErrorMessage(button)
            }
        } catch (error) {
            console.error('Error generating PDF:', error)
            this.showErrorMessage(button)
        }
    }

    drawPhotoPlaceholder(pdf, x, y, width, height) {
        pdf.setDrawColor(0, 0, 0)
        pdf.setFillColor(255, 255, 255)
        pdf.rect(x, y, width, height, 'FD')
    }

    async loadOrderPhotos() {
        try {
            // Usar las fotos declaradas en la vista
            if (!window.ORDER_PHOTOS || window.ORDER_PHOTOS.length === 0) {
                console.warn('No photos found in window.ORDER_PHOTOS')
                return []
            }
            
            console.log('Loading photos from window.ORDER_PHOTOS:', window.ORDER_PHOTOS)
            
            // Cargar cada foto y convertirla a base64
            const photos = []
            for (const photo of window.ORDER_PHOTOS) {
                try {
                    console.log(`Loading photo ${photo.id} from ${photo.url}`)
                    const photoResponse = await fetch(photo.url)
                    if (photoResponse.ok) {
                        const blob = await photoResponse.blob()
                        const dataUrl = await this.blobToBase64(blob)
                        photos.push({
                            id: photo.id,
                            url: photo.url,
                            dataUrl: dataUrl
                        })
                        console.log(`Successfully loaded photo ${photo.id}`)
                    } else {
                        console.error(`Failed to load photo ${photo.id}: ${photoResponse.status}`)
                    }
                } catch (error) {
                    console.error(`Error loading photo ${photo.id}:`, error)
                }
            }
            
            console.log(`Loaded ${photos.length} photos from window.ORDER_PHOTOS`)
            return photos
            
        } catch (error) {
            console.error('Error loading order photos:', error)
            return []
        }
    }

    async loadAndConvertLogo(svgUrl, targetWidthPx = null) {
        try {
            // Cargar el SVG
            const response = await fetch(svgUrl)
            const svgText = await response.text()

            // Crear un elemento SVG temporal para obtener viewBox
            const svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
            svgElement.innerHTML = svgText

            // Determinar tamaño del canvas basado en el URL o tamaño objetivo
            // Para printed-in.svg, usar resolución más alta (2000px base)
            // Para logos cuadrados, usar 1500px base
            let canvasWidth = 1000
            let canvasHeight = 1000
            
            if (svgUrl.includes('printed-in')) {
                // Aspect ratio de printed-in.svg: 58.4 / 31.92 ≈ 1.83
                canvasWidth = 2000
                canvasHeight = Math.round(2000 / 1.83)  // ≈ 1093
            } else if (svgUrl.includes('lelab-logo') && !svgUrl.includes('static')) {
                // Logo vertical de lelab: aspect ratio 31.9 / 9.96 ≈ 3.2
                canvasWidth = 1000
                canvasHeight = Math.round(1000 * 3.2)  // ≈ 3200
            }

            // Crear un canvas temporal
            const canvas = document.createElement('canvas')
            canvas.width = canvasWidth
            canvas.height = canvasHeight
            const ctx = canvas.getContext('2d')

            // Convertir SVG a PNG usando el canvas
            const img = new Image()
            const svgBlob = new Blob([svgText], { type: 'image/svg+xml' })
            const url = URL.createObjectURL(svgBlob)
            
            return new Promise((resolve, reject) => {
                img.onload = () => {
                    ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight)
                    URL.revokeObjectURL(url)
                    resolve(canvas.toDataURL('image/png'))
                }
                img.onerror = reject
                img.src = url
            })
        } catch (error) {
            console.error('Error loading logo:', error)
            // Retornar una imagen en blanco como fallback
            const canvas = document.createElement('canvas')
            canvas.width = 100
            canvas.height = 100
            return canvas.toDataURL('image/png')
        }
    }

    blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result)
            reader.onerror = reject
            reader.readAsDataURL(blob)
        })
    }

    showGeneratingMessage(link) {
        // Guardar el texto original del botón
        this.originalButtonText = link.innerHTML
        
        // Cambiar el texto del botón a "Generando..."
        link.innerHTML = '<i class="fa-solid fa-spinner fa-spin px-1"></i> Generando...'
        
        // Deshabilitar el botón
        link.style.pointerEvents = 'none'
        link.style.opacity = '0.7'
    }

    showReadyMessage(button) {
        // Restaurar el texto original
        if (this.originalButtonText) {
            button.innerHTML = this.originalButtonText
        }
        
        // Habilitar el botón
        button.style.pointerEvents = 'auto'
        button.style.opacity = '1'  
    }

    showErrorMessage(button) {
        // Restaurar el texto original
        if (this.originalButtonText) {
            button.innerHTML = this.originalButtonText
        }
        
        // Habilitar el botón
        button.style.pointerEvents = 'auto'
        button.style.opacity = '1'
    }

    /**
     * Genera un PDF de portada (cover) con todos los cálculos correctos
     * @param {Object} params - Parámetros del cover
     * @param {number} params.pageWidthCm - Ancho de la página en cm
     * @param {number} params.pageHeightCm - Alto de la página en cm
     * @param {number} params.spineWidthCm - Ancho del lomo en cm (por defecto 1cm)
     * @param {number} params.bleedCm - Márgenes de impresión (rebases) en cm (por defecto 1cm)
     * @param {Object} params.coverData - Datos del cover (título, subtítulo, etc.)
     * @param {string} params.fileName - Nombre del archivo PDF
     */
    async generateCoverPDF(params) {
        const {
            pageWidthCm,
            pageHeightCm,
            spineWidthCm = 1.0,
            bleedCm = 1.0,
            coverData = {},
            fileName = 'cover.pdf'
        } = params

        const dpi = 300
        const paperWidthMm = 330  // 33cm
        const paperHeightMm = 475 // 47.5cm

        try {
            // 1. Crear el layout del cover usando el servicio compartido
            const coverLayoutService = new CoverLayoutService()
            const layout = coverLayoutService.buildLayout({
                pageWidthCm,
                pageHeightCm,
                spineWidthCm,
                bleedCm,
                dpi
            })

            // 2. Obtener dimensiones del canvas completo (Portada + Lomo + Contraportada + Márgenes)
            const canvasSize = layout.config.getCanvasSize()
            const canvasWidthPx = canvasSize.width
            const canvasHeightPx = canvasSize.height

            // 3. Crear canvas HTML5 con dimensiones exactas a 300 DPI
            const canvas = document.createElement('canvas')
            canvas.width = canvasWidthPx
            canvas.height = canvasHeightPx
            const ctx = canvas.getContext('2d')

            // Configurar alta calidad de renderizado
            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = 'high'

            // 4. Obtener áreas del layout y bleed
            const { areas } = layout
            const { back, spine, front } = areas
            const { bleed, base } = canvasSize

            // 5. Obtener color de fondo
            const palette = coverData.palette || '#FFFFFF'
            const bgColor = palette.split(',')[0]?.trim() || '#FFFFFF'

            // 6. Dibujar fondo completo
            ctx.fillStyle = bgColor
            ctx.fillRect(0, 0, canvasWidthPx, canvasHeightPx)

            // 7. Cargar logos
            const logoTopImage = await this.loadAndConvertLogo('/static/lelab-logo.svg') // Logo pequeño arriba del lomo
            const logoBottomImage = await this.loadAndConvertLogo('/lelab-logo.svg') // Logo vertical abajo del lomo
            const backLogoImage = logoTopImage // Reutilizar para contraportada
            const printedInImage = await this.loadAndConvertLogo('/printed-in.svg')

            // 8. Obtener imágenes de las fotos del cover desde el DOM
            const coverPhotos = await this.loadCoverPhotos()

            // 9. Renderizar contraportada (back cover)
            await this.renderBackCover(ctx, back, layout, backLogoImage, printedInImage, coverData, dpi, bleed)

            // 10. Renderizar lomo (spine)
            await this.renderSpine(ctx, spine, layout, logoTopImage, logoBottomImage, coverData, dpi, bleed, base)

            // 11. Renderizar portada (front cover)
            await this.renderFrontCover(ctx, front, layout, coverData, dpi, bleed, base, coverPhotos)

            // 12. Dibujar marcas de corte y doblez
            this.renderCutMarks(ctx, layout.cutMarks)
            this.renderSpineMarks(ctx, layout.spineMarks)

            // 13. Convertir canvas a imagen
            const coverImageDataUrl = canvas.toDataURL('image/png', 1.0)

            // 12. Calcular dimensiones del cover en mm
            const pxToMm = (px) => (px * 25.4) / dpi
            const coverWidthMm = pxToMm(canvasWidthPx)
            const coverHeightMm = pxToMm(canvasHeightPx)

            // 13. Decidir orientación basada en si el cover cabe mejor en landscape o portrait
            // El cover NO debe escalarse por encima del 100% de su tamaño real
            // Solo se escala hacia abajo si no cabe en el papel
            
            // Opción 1: Portrait (330x475mm)
            const portraitScale = Math.min(
                330 / coverWidthMm,
                475 / coverHeightMm,
                1.0  // Máximo 100%, no ampliar
            )
            
            // Opción 2: Landscape (475x330mm)
            const landscapeScale = Math.min(
                475 / coverWidthMm,   // ancho del cover vs ancho del papel rotado
                330 / coverHeightMm,  // alto del cover vs alto del papel rotado
                1.0  // Máximo 100%, no ampliar
            )
            
            // Elegir orientación: landscape si se ajusta mejor (sin escala o menos escala)
            const useLandscape = landscapeScale >= portraitScale
            const orientation = useLandscape ? 'landscape' : 'portrait'
            
            // Usar las dimensiones correctas del papel según orientación
            let effectivePaperWidth = useLandscape ? 475 : 330     // mm
            let effectivePaperHeight = useLandscape ? 330 : 475    // mm
            
            const scale = useLandscape ? landscapeScale : portraitScale

            // 14. Calcular dimensiones finales escaladas
            const scaledWidthMm = coverWidthMm * scale
            const scaledHeightMm = coverHeightMm * scale

            // 15. Calcular offsets para centrar
            const offsetX = (effectivePaperWidth - scaledWidthMm) / 2
            const offsetY = (effectivePaperHeight - scaledHeightMm) / 2

            // 16. Crear PDF con orientación y dimensiones correctas
            const pdf = new jsPDF({
                orientation: orientation,
                unit: 'mm',
                format: useLandscape ? [effectivePaperHeight, effectivePaperWidth] : [effectivePaperWidth, effectivePaperHeight]
            })

            // 17. Agregar imagen del cover centrada
            pdf.addImage(
                coverImageDataUrl,
                'PNG',
                offsetX,
                offsetY,
                scaledWidthMm,
                scaledHeightMm
            )

            // 18. Guardar PDF
            pdf.save(fileName)

            console.log('PDF de cover generado correctamente:', {
                canvas: {
                    width: canvasWidthPx,
                    height: canvasHeightPx
                },
                cover: {
                    widthMm: coverWidthMm,
                    heightMm: coverHeightMm
                },
                paper: {
                    width: effectivePaperWidth,
                    height: effectivePaperHeight,
                    orientation: orientation
                },
                scale: scale,
                offset: {
                    x: offsetX,
                    y: offsetY
                },
                scaled: {
                    width: scaledWidthMm,
                    height: scaledHeightMm
                }
            })

        } catch (error) {
            console.error('Error generando PDF de cover:', error)
            throw error
        }
    }

    /**
     * Renderiza la contraportada en el canvas
     * Solo debe tener la imagen "Printed in México" en la parte inferior
     */
    async renderBackCover(ctx, backArea, layout, backLogoImage, printedInImage, coverData, dpi, bleed) {
        const pageWidthPx = layout.config.pageSize.toPixels(dpi).width
        const pageHeightPx = layout.config.pageSize.toPixels(dpi).height

        // Las áreas ya incluyen el offset del bleed
        const contentX = backArea.x
        const contentY = backArea.y

        // Solo dibujar imagen "Printed in México" en la parte inferior
        // Alineado con el logo inferior del lomo (spine_logo_bottom_y_calc del ERB)
        if (printedInImage) {
            // Calcular ancho basado en 4cm (como en el ERB)
            const printedInWidthCm = 4.0
            const printedInWidthPx = (printedInWidthCm * dpi) / 2.54
            
            // Aspect ratio correcto de printed-in.svg: 58.4 / 31.92
            const printedInAspectRatio = 58.4 / 31.92
            const printedInHeightPx = printedInWidthPx / printedInAspectRatio
            
            // Centrar horizontalmente
            const printedInX = contentX + (pageWidthPx - printedInWidthPx) / 2
            
            // Alinear verticalmente con el logo inferior del lomo (5% desde abajo + altura del logo del lomo)
            // Usar la misma fórmula que spine_logo_bottom_y del ERB
            const spineLogoBottomWidth = layout.config.spineSettings.toPixels(dpi) * 0.6
            const spineLogoBottomHeight = spineLogoBottomWidth * (31.9 / 9.96)
            const printedInY = contentY + pageHeightPx - (pageHeightPx * 0.05) - spineLogoBottomHeight

            const printedInImg = new Image()
            await new Promise((resolve, reject) => {
                printedInImg.onload = () => {
                    ctx.drawImage(printedInImg, printedInX, printedInY, printedInWidthPx, printedInHeightPx)
                    resolve()
                }
                printedInImg.onerror = reject
                printedInImg.src = printedInImage
            })
        }
    }

    /**
     * Renderiza el lomo en el canvas
     */
    async renderSpine(ctx, spineArea, layout, logoTopImage, logoBottomImage, coverData, dpi, bleed, base) {
        const spineWidthPx = layout.config.spineSettings.toPixels(dpi)
        const pageHeightPx = base.height // Usar altura base sin bleed

        // Las áreas ya incluyen el offset del bleed
        const contentX = spineArea.x
        const contentY = spineArea.y

        // 1. Logo superior pequeño (cuadrado, 80% del ancho del lomo) - /static/lelab-logo.svg
        if (logoTopImage) {
            const logoTopWidth = spineWidthPx * 0.8 // 80% del ancho del lomo (cuadrado)
            const logoTopHeight = logoTopWidth // Cuadrado
            const logoTopX = contentX + (spineWidthPx - logoTopWidth) / 2
            const logoTopY = contentY + (pageHeightPx * 0.05) // 5% de margen desde arriba

            const logoTopImg = new Image()
            await new Promise((resolve, reject) => {
                logoTopImg.onload = () => {
                    ctx.drawImage(logoTopImg, logoTopX, logoTopY, logoTopWidth, logoTopHeight)
                    resolve()
                }
                logoTopImg.onerror = reject
                logoTopImg.src = logoTopImage
            })
        }

        // 2. Texto del lomo vertical centrado (en medio del lomo)
        const spineText = coverData.spineText || 'Texto de lomo'
        const fontFamily = coverData.fontFamilies?.split(',')[0]?.trim() || 'Arial'
        const spineFontSizePx = spineWidthPx * 0.675 // 67.5% del ancho del lomo

        ctx.save()
        ctx.fillStyle = '#FFFFFF'
        ctx.font = `bold ${spineFontSizePx}px "${fontFamily}", sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'

        // Rotar el contexto para texto vertical
        // El texto está centrado verticalmente (igual que en el SVG)
        const textX = contentX + spineWidthPx / 2
        const textY = contentY + pageHeightPx / 2 // Centro vertical
        ctx.translate(textX, textY)
        ctx.rotate(-Math.PI / 2)
        ctx.fillText(spineText, 0, 0)
        ctx.restore()

        // 3. Logo inferior vertical (60% del ancho del lomo, aspect ratio 31.9/9.96) - /lelab-logo.svg
        if (logoBottomImage) {
            const logoBottomWidth = spineWidthPx * 0.6 // 60% del ancho del lomo
            const logoBottomAspectRatio = 31.9 / 9.96 // Logo vertical de LeLab
            const logoBottomHeight = logoBottomWidth * logoBottomAspectRatio
            const logoBottomX = contentX + (spineWidthPx - logoBottomWidth) / 2
            const logoBottomY = contentY + pageHeightPx - (pageHeightPx * 0.05) - logoBottomHeight // 5% de margen desde abajo

            const logoBottomImg = new Image()
            await new Promise((resolve, reject) => {
                logoBottomImg.onload = () => {
                    ctx.drawImage(logoBottomImg, logoBottomX, logoBottomY, logoBottomWidth, logoBottomHeight)
                    resolve()
                }
                logoBottomImg.onerror = reject
                logoBottomImg.src = logoBottomImage
            })
        }
    }

    /**
     * Renderiza la portada en el canvas
     */
    async renderFrontCover(ctx, frontArea, layout, coverData, dpi, bleed, base, coverPhotos) {
        const pageWidthPx = base.width // Usar ancho base sin bleed
        const pageHeightPx = base.height // Usar altura base sin bleed

        // Las áreas ya incluyen el offset del bleed
        const contentX = frontArea.x
        const contentY = frontArea.y

        // Renderizar formas del cover (fotos, etc.)
        await this.renderCoverShapes(ctx, contentX, contentY, coverData, pageWidthPx, pageHeightPx, coverPhotos)

        // Renderizar textos (título y subtítulo)
        await this.renderCoverText(ctx, contentX, contentY, coverData, pageWidthPx, pageHeightPx, dpi)
    }

    /**
     * Renderiza las formas del cover (rectángulos, círculos para fotos)
     */
    async renderCoverShapes(ctx, contentX, contentY, coverData, pageWidthPx, pageHeightPx, coverPhotos) {
        const shapes = this.parseCoverData(coverData.data || '')
        const colors = ['#55859a', '#f4a0a2', '#f9c7bc', '#da2830', '#28da31', '#000']

        for (let index = 0; index < shapes.length; index++) {
            const shape = shapes[index]
            const color = colors[index % colors.length]

            const x = contentX + (shape.left * pageWidthPx)
            const y = contentY + (shape.top * pageHeightPx)
            const w = shape.width * pageWidthPx
            const h = shape.height * pageHeightPx

            // Buscar si hay una foto para este slot
            const photo = coverPhotos.find(p => p.slotIndex === index)

            if (photo && photo.imageUrl) {
                // Cargar y dibujar la imagen
                try {
                    const img = await this.loadImageFromUrl(photo.imageUrl)
                    if (img) {
                        ctx.save()
                        
                        // Crear clip path según el tipo de forma
                        if (shape.type === 'c') {
                            // Círculo
                            ctx.beginPath()
                            ctx.arc(x + w / 2, y + h / 2, w / 2, 0, 2 * Math.PI)
                            ctx.clip()
                        } else {
                            // Rectángulo
                            ctx.beginPath()
                            ctx.rect(x, y, w, h)
                            ctx.clip()
                        }

                        // Dibujar imagen con object-fit: cover (xMidYMid slice)
                        const imgAspect = img.width / img.height
                        const slotAspect = w / h

                        let drawWidth, drawHeight, drawX, drawY

                        if (imgAspect > slotAspect) {
                            // Imagen más ancha - ajustar altura
                            drawHeight = h
                            drawWidth = h * imgAspect
                            drawX = x - (drawWidth - w) / 2
                            drawY = y
                        } else {
                            // Imagen más alta - ajustar ancho
                            drawWidth = w
                            drawHeight = w / imgAspect
                            drawX = x
                            drawY = y - (drawHeight - h) / 2
                        }

                        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
                        ctx.restore()

                        // Dibujar borde
                        ctx.strokeStyle = color
                        ctx.lineWidth = 2
                        if (shape.type === 'c') {
                            ctx.beginPath()
                            ctx.arc(x + w / 2, y + h / 2, w / 2, 0, 2 * Math.PI)
                            ctx.stroke()
                        } else {
                            ctx.strokeRect(x, y, w, h)
                        }
                    }
                } catch (error) {
                    console.error(`Error cargando imagen para slot ${index}:`, error)
                    // Fallback: dibujar placeholder
                    this.drawShapePlaceholder(ctx, shape.type, x, y, w, h, color)
                }
            } else {
                // No hay foto, dibujar placeholder
                this.drawShapePlaceholder(ctx, shape.type, x, y, w, h, color)
            }
        }
    }

    /**
     * Dibuja un placeholder para una forma
     */
    drawShapePlaceholder(ctx, type, x, y, w, h, color) {
        if (type === 'r') {
            // Rectángulo
            ctx.fillStyle = color
            ctx.fillRect(x, y, w, h)
            ctx.strokeStyle = color
            ctx.lineWidth = 2
            ctx.strokeRect(x, y, w, h)
        } else if (type === 'c') {
            // Círculo
            ctx.fillStyle = color
            ctx.beginPath()
            ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, 2 * Math.PI)
            ctx.fill()
            ctx.strokeStyle = color
            ctx.lineWidth = 2
            ctx.stroke()
        }
    }

    /**
     * Carga una imagen desde una URL
     */
    loadImageFromUrl(url) {
        return new Promise((resolve, reject) => {
            const img = new Image()
            img.crossOrigin = 'anonymous'
            img.onload = () => resolve(img)
            img.onerror = reject
            img.src = url
        })
    }

    /**
     * Carga las fotos del cover desde el DOM del SVG
     */
    async loadCoverPhotos() {
        const photos = []
        const svg = document.getElementById('svg-cover-details')
        if (!svg) return photos

        // Buscar grupos de slot para evitar duplicados (rect y group comparten clases).
        const photoSlots = svg.querySelectorAll('g.photo-slot.cover-photo-slot[data-slot-index]')

        for (const slot of photoSlots) {
            const slotIndex = parseInt(slot.getAttribute('data-slot-index'), 10)
            if (Number.isNaN(slotIndex)) continue

            const imgElement = slot.querySelector('image')
            if (!imgElement) continue

            const isPlaceholder = imgElement.classList.contains('photo-placeholder')
            if (isPlaceholder) continue

            const href = imgElement.getAttributeNS('http://www.w3.org/1999/xlink', 'href') ||
                        imgElement.getAttribute('href') ||
                        imgElement.getAttribute('xlink:href')

            if (!href) continue

            // Priorizar URL local asociada al fileIndex para evitar problemas CORS al exportar PDF.
            const fileIndexAttr = imgElement.getAttribute('data-file-index')
            let imageUrl = href

            if (fileIndexAttr !== null && window.fotobookPhotoSelector?.getSelectedFiles) {
                const fileIndex = parseInt(fileIndexAttr, 10)
                if (!Number.isNaN(fileIndex)) {
                    const selected = window.fotobookPhotoSelector.getSelectedFiles()[fileIndex]
                    if (selected?.localUrl) {
                        imageUrl = selected.localUrl
                    }
                }
            }

            photos.push({
                slotIndex,
                imageUrl
            })
        }

        return photos
    }

    /**
     * Renderiza las marcas de corte exteriores (en las 4 esquinas del canvas)
     */
    renderCutMarks(ctx, cutMarks) {
        if (!cutMarks || cutMarks.length === 0) return

        ctx.save()
        ctx.strokeStyle = '#000000'
        ctx.lineWidth = 1
        ctx.lineCap = 'butt'

        cutMarks.forEach(mark => {
            ctx.beginPath()
            ctx.moveTo(mark.x1, mark.y1)
            ctx.lineTo(mark.x2, mark.y2)
            ctx.stroke()
        })

        ctx.restore()
    }

    /**
     * Renderiza las marcas de doblez del lomo (en los bordes del lomo)
     */
    renderSpineMarks(ctx, spineMarks) {
        if (!spineMarks || spineMarks.length === 0) return

        ctx.save()
        ctx.strokeStyle = '#000000'
        ctx.lineWidth = 1
        ctx.lineCap = 'butt'

        spineMarks.forEach(mark => {
            ctx.beginPath()
            ctx.moveTo(mark.x1, mark.y1)
            ctx.lineTo(mark.x2, mark.y2)
            ctx.stroke()
        })

        ctx.restore()
    }

    /**
     * Renderiza los textos del cover (título y subtítulo)
     */
    async renderCoverText(ctx, contentX, contentY, coverData, pageWidthPx, pageHeightPx, dpi) {
        const titleText = coverData.title || ''
        const subtitleText = coverData.subtitle || ''

        if (!titleText && !subtitleText) return

        // Calcular tamaños de fuente
        const titleFontSizePx = (coverData.titleSize || 0.08) * pageWidthPx
        const subtitleFontSizePx = (coverData.subtitleSize || 0.06) * pageWidthPx
        const fontFamily = coverData.fontFamilies?.split(',')[0]?.trim() || 'Arial'

        // Crear SVG temporal para calcular posiciones
        const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
        tempSvg.setAttribute('width', pageWidthPx)
        tempSvg.setAttribute('height', pageHeightPx)
        document.body.appendChild(tempSvg)

        // Calcular posiciones usando la función compartida
        const positions = calculateTextPositionsInContainer({
            containerX: coverData.containerX || 0.1,
            containerY: coverData.containerY || 0.1,
            containerWidth: coverData.containerWidth || 0.8,
            containerHeight: coverData.containerHeight || 0.3,
            containerAlign: coverData.containerAlign || 'center-center',
            svgWidth: pageWidthPx,
            svgHeight: pageHeightPx,
            titleFontSize: titleFontSizePx,
            subtitleFontSize: subtitleFontSizePx,
            titleText,
            subtitleText,
            titleAutoFit: !coverData.titleWrap,
            titleWrap: coverData.titleWrap || false,
            subtitleAutoFit: !coverData.subtitleWrap,
            subtitleWrap: coverData.subtitleWrap || false,
            fontFamily: `"${fontFamily}", sans-serif`,
            titleFontWeight: 'bold',
            subtitleFontWeight: 'normal',
            svg: tempSvg
        })

        document.body.removeChild(tempSvg)

        // Renderizar título
        if (titleText && positions.title.lines.length > 0) {
            ctx.fillStyle = '#FFFFFF'
            ctx.font = `bold ${positions.title.fontSize}px "${fontFamily}", sans-serif`
            ctx.textAlign = positions.title.textAnchor === 'start' ? 'left' : 
                           positions.title.textAnchor === 'end' ? 'right' : 'center'
            ctx.textBaseline = 'middle'

            positions.title.lines.forEach((line, index) => {
                const lineY = positions.title.y + ((index - (positions.title.lines.length - 1) / 2) * positions.title.lineHeight)
                ctx.fillText(line, contentX + positions.title.x, contentY + lineY)
            })
        }

        // Renderizar subtítulo
        if (subtitleText && positions.subtitle.lines.length > 0) {
            ctx.fillStyle = '#FFFFFF'
            ctx.font = `${positions.subtitle.fontSize}px "${fontFamily}", sans-serif`
            ctx.textAlign = positions.subtitle.textAnchor === 'start' ? 'left' : 
                           positions.subtitle.textAnchor === 'end' ? 'right' : 'center'
            ctx.textBaseline = 'middle'

            positions.subtitle.lines.forEach((line, index) => {
                const lineY = positions.subtitle.y + ((index - (positions.subtitle.lines.length - 1) / 2) * positions.subtitle.lineHeight)
                ctx.fillText(line, contentX + positions.subtitle.x, contentY + lineY)
            })
        }
    }

    /**
     * Parsea el string de datos del cover para obtener las formas
     */
    parseCoverData(dataString) {
        if (!dataString || dataString.trim() === '') return []

        const shapes = []
        const parts = dataString.split('|')

        parts.forEach((part) => {
            const trimmed = part.trim()
            if (!trimmed) return

            const match = trimmed.match(/^([rc]):([\d.]+),([\d.]+),([\d.]+),([\d.]+)$/)
            if (match) {
                shapes.push({
                    type: match[1],
                    width: parseFloat(match[2]),
                    height: parseFloat(match[3]),
                    top: parseFloat(match[4]),
                    left: parseFloat(match[5])
                })
            }
        })

        return shapes
    }

    /**
     * Convierte color hex a RGB
     */
    hexToRgb(hex) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
        return result
            ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
            : [255, 255, 255]
    }
} 